import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
    RebelColors,
    RebelAccentButton,
    RebelIcon,
    RebelModal,
    RebelModalHeader,
    RebelModalBody,
    RebelModalFooter
} from '../../../styles/rebel-experiences';
import { GetDB } from '../../../utils/database/firebase-budget-tool';
import { listenToCollection, listenToDocument, updateDocumentProperty } from '../../../utils/firestore-functions';

const RebelVillaBasicPricingTab = ({ villaId }) => {
    const [categories, setCategories] = useState([]);
    const [pricingData, setPricingData] = useState({});
    const [originalPricingData, setOriginalPricingData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savedModal, setSavedModal] = useState(false);

    const budgetDB = GetDB();

    // Listen to categories from agenda-block-types
    useEffect(() => {
        const unsubscribeCategories = listenToCollection(budgetDB, "agenda-block-types", (data) => {
            if (data && data.length > 0) {
                const sortedCategories = data.sort((a, b) => {
                    if (a.order !== undefined && b.order !== undefined) {
                        return a.order - b.order;
                    }
                    return (a.name || '').localeCompare(b.name || '');
                });
                setCategories(sortedCategories);
            } else {
                setCategories([]);
            }
            setLoading(false);
        });

        return () => {
            if (unsubscribeCategories) unsubscribeCategories();
        };
    }, [budgetDB]);

    // Normalize pricing data to flat format (handles old nested format)
    const normalizePricingData = (data) => {
        if (!data) return {};
        const normalized = {};
        Object.entries(data).forEach(([categoryId, value]) => {
            if (typeof value === 'object' && value !== null) {
                // Old nested format - take first value
                const values = Object.values(value).filter(v => typeof v === 'number');
                normalized[categoryId] = values.length > 0 ? values[0] : 0;
            } else {
                // New flat format
                normalized[categoryId] = typeof value === 'number' ? value : 0;
            }
        });
        return normalized;
    };

    // Listen to villa document for basicViewPricing
    useEffect(() => {
        const unsubscribeVilla = listenToDocument(budgetDB, "rebelVillas", villaId, (villa) => {
            if (villa && villa.basicViewPricing) {
                const normalized = normalizePricingData(villa.basicViewPricing);
                setPricingData(normalized);
                setOriginalPricingData(normalized);
            } else {
                setPricingData({});
                setOriginalPricingData({});
            }
        });

        return () => {
            if (unsubscribeVilla) unsubscribeVilla();
        };
    }, [villaId, budgetDB]);

    const handlePriceChange = (categoryId, value) => {
        // Allow any input while typing (including empty string)
        setPricingData(prev => ({
            ...prev,
            [categoryId]: value
        }));
    };

    const handlePriceBlur = (categoryId) => {
        // Validate and convert to number on blur
        const currentValue = pricingData[categoryId];
        const numValue = parseFloat(currentValue) || 0;
        setPricingData(prev => ({
            ...prev,
            [categoryId]: numValue
        }));
    };

    const getPrice = (categoryId) => {
        const value = pricingData[categoryId];
        // Return empty string if it's empty, otherwise return the value
        if (value === '' || value === undefined || value === null) {
            return '';
        }
        return value;
    };

    const hasSavedPrice = (categoryId) => {
        return originalPricingData[categoryId] !== undefined && originalPricingData[categoryId] > 0;
    };

    const hasUnsavedChanges = (categoryId) => {
        const current = parseFloat(pricingData[categoryId]) || 0;
        const original = parseFloat(originalPricingData[categoryId]) || 0;
        return current !== original;
    };

    const savePricing = async () => {
        setSaving(true);
        try {
            // Build complete pricing object with flat category prices (convert to numbers)
            const completePricingData = {};
            categories.forEach(category => {
                completePricingData[category.id] = parseFloat(pricingData[category.id]) || 0;
            });

            await new Promise((resolve, reject) => {
                updateDocumentProperty(
                    budgetDB,
                    "rebelVillas",
                    villaId,
                    "basicViewPricing",
                    completePricingData,
                    () => resolve(),
                    (error) => reject(error)
                );
            });
            setPricingData(completePricingData);
            setOriginalPricingData(completePricingData);
            setSavedModal(true);
        } catch (error) {
            console.error('Error saving pricing:', error);
            alert('Error saving pricing. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // Calculate totals for summary
    const totalCategories = categories.length;
    const configuredCategories = categories.filter(cat => hasSavedPrice(cat.id)).length;

    if (loading) {
        return (
            <LoadingContainer>
                <p>Loading pricing configuration...</p>
            </LoadingContainer>
        );
    }

    return (
        <React.Fragment>
            <RebelModal isOpen={savedModal} toggle={() => setSavedModal(false)}>
                <RebelModalHeader>Saved</RebelModalHeader>
                <RebelModalBody>
                    Successfully saved basic view pricing data.
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelAccentButton onClick={() => setSavedModal(false)}>
                        Okay
                    </RebelAccentButton>
                </RebelModalFooter>
            </RebelModal>

            <PricingContainer>
                <HeaderRow>
                    <HeaderContent>
                        <SectionTitle>
                            <RebelIcon icon={['fas', 'tags']} />
                            Basic View Pricing
                        </SectionTitle>
                        <SectionDescription>
                            Set per-person costs for each activity category used in the basic agenda view.
                        </SectionDescription>
                    </HeaderContent>
                    <HeaderStats>
                        <StatBadge configured={configuredCategories === totalCategories}>
                            <span>{configuredCategories}</span> / <span>{totalCategories}</span> configured
                        </StatBadge>
                    </HeaderStats>
                </HeaderRow>

                {categories.length === 0 ? (
                    <EmptyState>
                        <RebelIcon icon={['fas', 'tags']} style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
                        <p>No categories found. Please configure categories in the Config tab first.</p>
                    </EmptyState>
                ) : (
                    <PricingGrid>
                        {categories.map((category) => (
                            <PricingCard key={category.id} hasChanges={hasUnsavedChanges(category.id)}>
                                <CardHeader>
                                    <CategoryName>{category.name}</CategoryName>
                                    <StatusIndicator status={
                                        hasUnsavedChanges(category.id) ? 'modified' :
                                        hasSavedPrice(category.id) ? 'configured' : 'empty'
                                    }>
                                        <RebelIcon icon={['fas',
                                            hasUnsavedChanges(category.id) ? 'circle' :
                                            hasSavedPrice(category.id) ? 'check-circle' : 'minus-circle'
                                        ]} />
                                    </StatusIndicator>
                                </CardHeader>
                                <PriceInputGroup
                                    hasChanges={hasUnsavedChanges(category.id)}
                                    onClick={(e) => {
                                        const input = e.currentTarget.querySelector('input');
                                        if (input) input.focus();
                                    }}
                                >
                                    <PricePrefix>$</PricePrefix>
                                    <PriceInput
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={getPrice(category.id)}
                                        onChange={(e) => handlePriceChange(category.id, e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        onBlur={() => handlePriceBlur(category.id)}
                                        placeholder="0.00"
                                    />
                                    <PriceSuffix>/person</PriceSuffix>
                                </PriceInputGroup>
                            </PricingCard>
                        ))}
                    </PricingGrid>
                )}

                <FooterRow>
                    <FooterHint>
                        <RebelIcon icon={['fas', 'info-circle']} />
                        Prices are applied per person for activities in the basic agenda view
                    </FooterHint>
                    <RebelAccentButton onClick={savePricing} disabled={saving}>
                        <RebelIcon icon={['fas', 'save']} />
                        {saving ? 'Saving...' : 'Save Pricing'}
                    </RebelAccentButton>
                </FooterRow>
            </PricingContainer>
        </React.Fragment>
    );
};

export default RebelVillaBasicPricingTab;

const LoadingContainer = styled.div`
    text-align: center;
    padding: 48px;
    color: ${RebelColors.primaryMuted};
`;

const PricingContainer = styled.div`
    padding: 24px;
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid ${RebelColors.gold}40;
    gap: 20px;
    flex-wrap: wrap;
`;

const HeaderContent = styled.div`
    flex: 1;
    min-width: 200px;
`;

const SectionTitle = styled.h3`
    color: ${RebelColors.gold};
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    gap: 10px;

    svg {
        color: ${RebelColors.gold};
    }
`;

const SectionDescription = styled.p`
    color: ${RebelColors.primaryMuted};
    font-size: 14px;
    margin: 0;
    line-height: 1.5;
`;

const HeaderStats = styled.div`
    display: flex;
    align-items: center;
`;

const StatBadge = styled.div`
    background: ${props => props.configured
        ? `linear-gradient(135deg, ${RebelColors.gold}20 0%, ${RebelColors.gold}10 100%)`
        : 'rgba(255, 255, 255, 0.05)'
    };
    border: 1px solid ${props => props.configured ? RebelColors.gold : RebelColors.border};
    color: ${props => props.configured ? RebelColors.gold : RebelColors.primaryMuted};
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;

    span:first-child {
        font-weight: 700;
        color: ${props => props.configured ? RebelColors.gold : RebelColors.text};
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 60px 20px;
    color: ${RebelColors.primaryMuted};
    background: rgba(255, 255, 255, 0.02);
    border-radius: 8px;
    border: 2px dashed ${RebelColors.border};

    p {
        margin-top: 16px;
        font-style: italic;
    }
`;

const PricingGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
    margin-bottom: 20px;
`;

const PricingCard = styled.div`
    background: ${RebelColors.surface};
    border: 1px solid ${props => props.hasChanges ? RebelColors.gold : RebelColors.border};
    border-radius: 10px;
    padding: 14px;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${RebelColors.gold}80;
        box-shadow: 0 2px 8px rgba(255, 215, 0, 0.1);
    }
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
`;

const CategoryName = styled.span`
    color: ${RebelColors.text};
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const PriceInputGroup = styled.div`
    display: flex;
    align-items: center;
    background: ${RebelColors.background};
    border: 1px solid ${props => props.hasChanges ? RebelColors.gold : RebelColors.border};
    border-radius: 6px;
    padding: 0 10px;
    transition: all 0.2s ease;
    cursor: text;

    &:focus-within {
        border-color: ${RebelColors.gold};
        box-shadow: 0 0 0 2px ${RebelColors.gold}20;
    }

    &:hover {
        border-color: ${RebelColors.gold}80;
    }
`;

const PricePrefix = styled.span`
    color: ${RebelColors.gold};
    font-size: 13px;
    font-weight: 600;
`;

const PriceInput = styled.input`
    background: transparent;
    border: none;
    color: ${RebelColors.text};
    padding: 8px 4px;
    font-size: 13px;
    width: 50px;
    text-align: right;
    font-weight: 500;

    &:focus {
        outline: none;
    }

    &::placeholder {
        color: ${RebelColors.primaryMuted};
    }

    /* Hide spinner buttons */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    -moz-appearance: textfield;
`;

const PriceSuffix = styled.span`
    color: ${RebelColors.primaryMuted};
    font-size: 11px;
    margin-left: 2px;
`;

const StatusIndicator = styled.div`
    flex-shrink: 0;

    svg {
        font-size: 12px;
        color: ${props => {
            switch(props.status) {
                case 'configured': return '#4caf50';
                case 'modified': return RebelColors.gold;
                case 'empty': return RebelColors.primaryMuted;
                default: return RebelColors.primaryMuted;
            }
        }};
    }
`;

const FooterRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 16px;
    gap: 16px;
    flex-wrap: wrap;
`;

const FooterHint = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${RebelColors.primaryMuted};
    font-size: 13px;

    svg {
        color: ${RebelColors.gold};
        font-size: 14px;
    }
`;
